/* Copyright (c) 2020-present CloudIO Inc */

import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { isNotEmpty } from 'utils/isEmpty';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './CalendarHeatmap';
import { helpRenders } from './CalendarHeatmap_Help';

const CalendarHeatmapManager: ComponentManagerFn<'CalendarHeatmap'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'CalendarHeatmap'>(platform, 'CalendarHeatmap'),
    type: 'CalendarHeatmap',
    name: 'Calendar Heatmap',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      comboDatasourceAlias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Data',
        allowedTypes: ['CloudDataStore', 'LocalDataStore', 'ReadOnlyDataStore'],
      },
      dateAttribute: {
        label: 'Date Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'LOV',
        getAttributeDataTypes: () => ['Date'],
        preventMultiEdit: true,
      },
      valueAttribute: {
        label: 'Value Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'LOV',
        getAttributeDataTypes: () => ['Integer', 'Double', 'Decimal'],
        preventMultiEdit: true,
      },
      groupFunction: {
        label: 'Group Function',
        type: 'select',
        options: toOptions(['Avg', 'Count', 'Max', 'Min', 'Sum']),
        required: true,
        category: 'Data',
      },
      showMonthLabels: {
        label: 'Show Month Labels',
        type: 'boolean',
        category: 'Labels',
      },
      showWeekdayLabels: {
        label: 'Show Weekday Labels',
        type: 'boolean',
        category: 'Labels',
      },
      showOutOfRangeDays: {
        label: 'Show Out Of Range Days',
        type: 'boolean',
      },
      color1MaxValue: {
        label: 'Color 1 Max Value',
        type: 'numberInput',
        category: 'Color',
        required: true,
        helpText:
          'Enter a value upto which the first color to be used in the heatmap.',
      },
      color2MaxValue: {
        label: 'Color 2 Max Value',
        type: 'numberInput',
        required: true,
        category: 'Color',
        helpText:
          'Enter a value upto which the 2nd color color to be used in the heatmap.',
      },
      color3MaxValue: {
        label: 'Color 3 Max Value',
        type: 'numberInput',
        category: 'Color',
        required: true,
        helpText:
          'Enter a value upto which the 3rd color color to be used in the heatmap.',
      },
      color4MaxValue: {
        label: 'Color 4 Max Value',
        type: 'numberInput',
        category: 'Color',
        required: true,
        helpText:
          'Enter a value upto which the last color to be used in the heatmap. Every other value higher than this will be shown as black',
      },
      fromDateRange: {
        label: 'From Date Range',
        type: 'number',
        category: 'Data',
        min: -365,
        max: 0,
        step: 7,
        required: true,
        helpText:
          'Enter the number of days to go back for the range. e.g. -365 to show for last one year.',
      },
      toDateRange: {
        label: 'To Date Range',
        type: 'number',
        category: 'Data',
        min: 0,
        max: 365,
        step: 7,
        required: true,
        helpText:
          'Enter 0 to show till today or any positive number to show in the future.',
      },
      toolTip: {
        label: 'Tooltip',
        type: 'expressionOnly',
        returnType: () => 'string',
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'dateAttribute':
        case 'valueAttribute':
        case 'groupFunction':
          return isNotEmpty(itemData.comboDatasourceAlias);
        default:
          return true;
      }
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'comboDatasourceAlias' } : undefined,
    isDataSourceAliasUsed: (item, alias) =>
      item.props.comboDatasourceAlias === alias,
    getDisplayText: (item) => item.props.dateAttribute,
    category: 'Dashboard',
    icon: { iconName: 'calendar-alt' },
    description: `**Overview**<br/>
The Calendar Heatmap is a powerful data visualization tool designed to reveal time-based patterns, trends, and anomalies. By displaying data in a grid format—where days are colored according to metric intensity—it allows users to instantly spot high-activity periods (e.g., "Why are sales spiking every Tuesday?") or gaps in performance.
Commonly seen in systems like GitHub (contribution graphs), this component is ideal for tracking daily metrics such as login activity, error logs, transaction volumes, or attendance records over long periods.

**1. Data Configuration**<br/>
The heatmap requires a structured data source to map dates to values.
* **Data Store Alias:** Select the data source (Cloud, Local, or Read-Only) containing the records you wish to analyze.
* **Date Attribute:** Select the field representing the "Day." This determines the position of the cell in the calendar grid.
* **Value Attribute:** Select the numeric field representing the "Intensity." This determines the color of the cell.
* **Group Function:** Since multiple records often exist for a single day (e.g., 50 separate sales transactions), you must define how to aggregate them into a single score for that day. (Options: Sum, Count, Average, Max, Min).

**2. Defining the Time Window**<br/>
The range of the heatmap is defined relative to the current date ("Today"), allowing the view to stay relevant automatically as time progresses.
* **From Date Range:** Sets the start date as a negative offset from today. (Example: Enter -365 to show data starting from one year ago).
* **To Date Range:** Sets the end date as a positive offset from today. (Example: Enter 0 to end today, or 30 to project the grid into next month).
* **Show Out Of Range Days:** If enabled, days outside the configured range (but within the visible month blocks) will be rendered, providing a complete calendar view even if data isn't loaded for those specific edge days.

**3. Intensity & Color Thresholds**<br/>
The heatmap uses a 4-stage color scale to represent data density. You must define the upper limit for each stage to calibrate the visualization to your specific data volume.
* **Color 1 Max Value:** Values from 0 up to this number get the lightest color.
* **Color 2 Max Value:** Values between Color 1 and this number get the medium-light color.
* **Color 3 Max Value:** Values between Color 2 and this number get the medium-dark color.
* **Color 4 Max Value:** Values between Color 3 and this number get the darkest color (highest intensity).
* **Note:** Any value exceeding the Color 4 Max Value will be rendered in Black (or the theme's maximum contrast color), indicating an outlier or extreme peak.

**4. Visuals & Interaction**<br/>
* **Labels:** You can toggle Show Month Labels and Show Weekday Labels to provide context or remove them for a minimalist "sparkline" aesthetic.
* **Tooltip:** Define a custom expression (e.g., "Sales: " + value) that appears when a user hovers over a specific day cell.
* **Interactivity:** The component supports TriggerOnClick. You can configure a workflow to run when a user clicks a specific day—for example, opening a drill-down list showing all transactions for that date.`,
    helpRenders,
  };
};

export default CalendarHeatmapManager;
